import React, { useEffect, useMemo, useState } from "react";
import { Modal, Button } from "react-bootstrap";

import { postRemito } from "../helpers/rutaRemitos";
import { getProveedores } from "../helpers/rutaProveedores";
import { getClientes } from "../helpers/rutaClientes";
import { getTransportes } from "../helpers/rutaTransportes";
import { getChoferes } from "../helpers/rutaChoferes";
import { getLotes } from "../helpers/rutaLotes";

/* =========================================================
   UTILS
========================================================= */

const sortBy = (arr, key) =>
  [...(arr || [])].sort((a, b) =>
    (a?.[key] || "").localeCompare(b?.[key] || "", "es", {
      sensitivity: "base",
    })
  );

const VARIEDADES_SUGERIDAS = [
  "ATLANTIC",
  "INNOVATOR",
  "SPUNTA",
  "BATATA",
];

const todayLocalYMD = () => {
  const d = new Date();
  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 10);
};

const makeInitialForm = () => ({
  fecha: todayLocalYMD(),

  kgCampo: "",
  kgRecibido: "",

  precioCompra: "",
  precioVenta: "",

  proveedor: "",
  cliente: "",
  transporte: "",
  chofer: "",
  lote: "",

  variedad: "",

  nroDTVCompra: "",
  nroDTVVenta: "",

  nota: "",

  factura: true,

  // Forma de distribución
  modoCompra: "PORCENTAJE",
  modoVenta: "PORCENTAJE",

  // % correspondiente a B
  porcentajeCompra: 100,
  porcentajeVenta: 100,

  // tarifa de flete por kg
  flete: 0,

  facturacompra: "",
  facturaventa: "",

  // Valores manuales modo MONTO
  compraAManual: "",
  compraBManual: "",
  ventaAManual: "",
  ventaBManual: "",

  // Indica si el valor fue autocompletado
  autoCompraA: false,
  autoCompraB: false,
  autoVentaA: false,
  autoVentaB: false,
});

function parseNumber(value, def = 0) {
  if (
    value === "" ||
    value === null ||
    value === undefined
  ) {
    return def;
  }

  if (typeof value === "number") {
    return Number.isFinite(value) ? value : def;
  }

  let s = String(value)
    .trim()
    .replace(/\$/g, "")
    .replace(/\s/g, "");

  if (!s) return def;

  // Formato argentino: 1.234.567,89
  if (s.includes(",")) {
    s = s.replace(/\./g, "").replace(",", ".");
  }

  const n = Number(s);

  return Number.isFinite(n) ? n : def;
}

const clamp = (n, min, max) =>
  Math.min(Math.max(n, min), max);

const to2 = (n) =>
  Number.isFinite(Number(n))
    ? Number(n).toFixed(2)
    : "0.00";

const formatES = (
  value,
  {
    decimals = 2,
    currency = false,
  } = {}
) => {
  if (
    value === "" ||
    value === null ||
    value === undefined
  ) {
    return "";
  }

  const n = parseNumber(value, 0);

  const formatted = n.toLocaleString("es-AR", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });

  return currency
    ? `$ ${formatted}`
    : formatted;
};

/* =========================================================
   INPUT NUMÉRICO FORMATEADO

   - Al no estar editando:
       $ 2.500.000,00
       10.000

   - Al hacer foco:
       2500000
       10000

   El estado siempre conserva número limpio.
========================================================= */

function FormattedNumberInput({
  name,
  value,
  onChange,
  decimals = 2,
  currency = false,
  allowDecimals = true,
  allowNegative = false,
  readOnly = false,
  required = false,
  className = "form-control",
  style,
}) {
  const [focused, setFocused] = useState(false);
  const [draft, setDraft] = useState("");

  useEffect(() => {
    if (!focused) {
      setDraft(
        value === "" ||
          value === null ||
          value === undefined
          ? ""
          : String(value)
      );
    }
  }, [value, focused]);

  const normalizar = (texto) => {
    let s = String(texto ?? "")
      .replace(/\$/g, "")
      .replace(/\s/g, "")
      .replace(/[^\d,.\-]/g, "");

    if (!allowNegative) {
      s = s.replace(/-/g, "");
    } else {
      const negativo = s.startsWith("-");
      s = s.replace(/-/g, "");
      if (negativo) s = `-${s}`;
    }

    if (!s) return "";

    if (!allowDecimals) {
      s = s
        .replace(/\./g, "")
        .replace(/,/g, "");

      return s;
    }

    /*
      Si escribió coma:
      1.234,56 -> 1234.56
    */
    if (s.includes(",")) {
      const negativo = s.startsWith("-");
      if (negativo) s = s.slice(1);

      const partes = s.split(",");
      const entero = (partes.shift() || "").replace(/\./g, "");
      const decimales = partes
        .join("")
        .replace(/\./g, "")
        .slice(0, 2);

      const result =
        decimales !== ""
          ? `${entero}.${decimales}`
          : entero;

      return negativo ? `-${result}` : result;
    }

    /*
      Si escribió punto como decimal:
      130.5 -> 130.5

      Si viene estilo miles:
      2.500 -> 2500
    */
    const negativo = s.startsWith("-");
    if (negativo) s = s.slice(1);

    const puntos = (s.match(/\./g) || []).length;

    let result = s;

    if (puntos > 1) {
      result = s.replace(/\./g, "");
    } else if (puntos === 1) {
      const [entero, decimal = ""] = s.split(".");

      if (decimal.length > 2) {
        // probablemente separador de miles
        result = `${entero}${decimal}`;
      } else {
        result = s;
      }
    }

    return negativo ? `-${result}` : result;
  };

  const handleFocus = () => {
    if (readOnly) return;

    setFocused(true);

    setDraft(
      value === "" ||
        value === null ||
        value === undefined
        ? ""
        : String(value)
    );
  };

  const handleChange = (e) => {
    if (readOnly) return;

    const texto = e.target.value;

    setDraft(texto);

    const raw = normalizar(texto);

    if (onChange) {
      onChange({
        target: {
          name,
          value: raw,
          type: "text",
          checked: false,
        },
      });
    }
  };

  const handleBlur = () => {
    setFocused(false);
  };

  const displayValue =
    readOnly || !focused
      ? formatES(value, {
          decimals,
          currency,
        })
      : draft;

  return (
    <input
      type="text"
      inputMode={allowDecimals ? "decimal" : "numeric"}
      name={name}
      className={className}
      value={displayValue}
      onFocus={handleFocus}
      onChange={handleChange}
      onBlur={handleBlur}
      readOnly={readOnly}
      required={required}
      style={style}
      autoComplete="off"
    />
  );
}

/* =========================================================
   COMPONENTE
========================================================= */

const AddRemitoForm = ({
  show,
  onClose,
}) => {
  const [okModal, setOkModal] = useState(false);

  const [form, setForm] = useState(makeInitialForm());

  const [lists, setLists] = useState({
    prov: [],
    cli: [],
    tra: [],
    chof: [],
    lot: [],
    loading: true,
    error: "",
  });

  /* =========================================================
     LISTAS
  ========================================================= */

  useEffect(() => {
    (async () => {
      try {
        const [
          prov,
          cli,
          tra,
          chof,
          lot,
        ] = await Promise.all([
          getProveedores(),
          getClientes(),
          getTransportes(),
          getChoferes(),
          getLotes(),
        ]);

        setLists({
          prov: Array.isArray(prov)
            ? prov
            : prov?.proveedores || [],

          cli: Array.isArray(cli)
            ? cli
            : cli?.clientes || [],

          tra: Array.isArray(tra)
            ? tra
            : tra?.transportes || [],

          chof: Array.isArray(chof)
            ? chof
            : chof?.choferes || [],

          lot: Array.isArray(lot)
            ? lot
            : lot?.lotes || [],

          loading: false,
          error: "",
        });
      } catch (e) {
        setLists((s) => ({
          ...s,
          loading: false,
          error:
            e?.message ||
            "Error al cargar listas",
        }));
      }
    })();
  }, []);

  const provOpts = useMemo(
    () => sortBy(lists.prov, "razonsocial"),
    [lists.prov]
  );

  const cliOpts = useMemo(
    () => sortBy(lists.cli, "razonsocial"),
    [lists.cli]
  );

  const traOpts = useMemo(
    () => sortBy(lists.tra, "transporte"),
    [lists.tra]
  );

  const chofOpts = useMemo(
    () => sortBy(lists.chof, "nombre"),
    [lists.chof]
  );

  const lotOpts = useMemo(
    () => sortBy(lists.lot, "lote"),
    [lists.lot]
  );

  /* =========================================================
     HANDLER GENERAL
  ========================================================= */

  const handle = (e) => {
    const {
      name,
      value,
      type,
      checked,
    } = e.target;

    setForm((prev) => ({
      ...prev,

      [name]:
        type === "checkbox"
          ? checked
          : value,
    }));
  };

  /* =========================================================
     CALCULOS BASE
  ========================================================= */

  const kgRecibidoNum =
    parseNumber(form.kgRecibido);

  const kgCampoNum =
    parseNumber(form.kgCampo);

  const diferencia =
    kgCampoNum -
    kgRecibidoNum;

  const precioCompraNum =
    parseNumber(form.precioCompra);

  const precioVentaNum =
    parseNumber(form.precioVenta);

  // Base SIN IVA
  const totalCompra =
    kgRecibidoNum *
    precioCompraNum;

  const totalVenta =
    kgRecibidoNum *
    precioVentaNum;

  const comision =
    totalVenta -
    totalCompra;

  const IVA_105 = 0.105;

  const FACTOR_IVA =
    1 + IVA_105;

  /* =========================================================
     POR PORCENTAJE
  ========================================================= */

  const pctCompra =
    parseNumber(
      form.porcentajeCompra
    );

  const pctVenta =
    parseNumber(
      form.porcentajeVenta
    );

  const fracCompraNegro =
    clamp(
      pctCompra / 100,
      0,
      1
    );

  const fracVentaNegro =
    clamp(
      pctVenta / 100,
      0,
      1
    );

  const fracCompraBlanco =
    1 -
    fracCompraNegro;

  const fracVentaBlanco =
    1 -
    fracVentaNegro;

  const compraB_calc =
    totalCompra *
    fracCompraNegro;

  const compraA_calc =
    totalCompra *
    FACTOR_IVA *
    fracCompraBlanco;

  const ventaB_calc =
    totalVenta *
    fracVentaNegro;

  const ventaA_calc =
    totalVenta *
    FACTOR_IVA *
    fracVentaBlanco;

  /* =========================================================
     BASES MODO MONTO
  ========================================================= */

  const baseCompra =
    Math.max(
      0,
      totalCompra
    );

  const baseVenta =
    Math.max(
      0,
      totalVenta
    );

  const calcCompraAFromB = (b) =>
    (
      baseCompra -
      clamp(
        b,
        0,
        baseCompra
      )
    ) * FACTOR_IVA;

  const calcCompraBFromA = (a) =>
    baseCompra -
    clamp(
      a / FACTOR_IVA,
      0,
      baseCompra
    );

  const calcVentaAFromB = (b) =>
    (
      baseVenta -
      clamp(
        b,
        0,
        baseVenta
      )
    ) * FACTOR_IVA;

  const calcVentaBFromA = (a) =>
    baseVenta -
    clamp(
      a / FACTOR_IVA,
      0,
      baseVenta
    );

  /* =========================================================
     MANUAL COMPRA
  ========================================================= */

  const handleCompraBManual = (e) => {
    const v = e.target.value;

    setForm((prev) => {
      const next = {
        ...prev,
        compraBManual: v,
        autoCompraB: false,
      };

      const bNum =
        v === ""
          ? 0
          : parseNumber(v);

      if (
        (
          prev.compraAManual === "" ||
          prev.autoCompraA
        ) &&
        baseCompra > 0
      ) {
        next.compraAManual =
          v === ""
            ? ""
            : to2(
                calcCompraAFromB(
                  bNum
                )
              );

        next.autoCompraA =
          v !== "";
      }

      return next;
    });
  };

  const handleCompraAManual = (e) => {
    const v = e.target.value;

    setForm((prev) => {
      const next = {
        ...prev,
        compraAManual: v,
        autoCompraA: false,
      };

      const aNum =
        v === ""
          ? 0
          : parseNumber(v);

      if (
        (
          prev.compraBManual === "" ||
          prev.autoCompraB
        ) &&
        baseCompra > 0
      ) {
        next.compraBManual =
          v === ""
            ? ""
            : to2(
                calcCompraBFromA(
                  aNum
                )
              );

        next.autoCompraB =
          v !== "";
      }

      return next;
    });
  };

  /* =========================================================
     MANUAL VENTA
  ========================================================= */

  const handleVentaBManual = (e) => {
    const v = e.target.value;

    setForm((prev) => {
      const next = {
        ...prev,
        ventaBManual: v,
        autoVentaB: false,
      };

      const bNum =
        v === ""
          ? 0
          : parseNumber(v);

      if (
        (
          prev.ventaAManual === "" ||
          prev.autoVentaA
        ) &&
        baseVenta > 0
      ) {
        next.ventaAManual =
          v === ""
            ? ""
            : to2(
                calcVentaAFromB(
                  bNum
                )
              );

        next.autoVentaA =
          v !== "";
      }

      return next;
    });
  };

  const handleVentaAManual = (e) => {
    const v = e.target.value;

    setForm((prev) => {
      const next = {
        ...prev,
        ventaAManual: v,
        autoVentaA: false,
      };

      const aNum =
        v === ""
          ? 0
          : parseNumber(v);

      if (
        (
          prev.ventaBManual === "" ||
          prev.autoVentaB
        ) &&
        baseVenta > 0
      ) {
        next.ventaBManual =
          v === ""
            ? ""
            : to2(
                calcVentaBFromA(
                  aNum
                )
              );

        next.autoVentaB =
          v !== "";
      }

      return next;
    });
  };

  /* =========================================================
     VALORES FINALES
  ========================================================= */

  const compraA_final =
    form.modoCompra === "MONTO"
      ? form.compraAManual !== ""
        ? parseNumber(
            form.compraAManual
          )
        : compraA_calc
      : compraA_calc;

  const compraB_final =
    form.modoCompra === "MONTO"
      ? form.compraBManual !== ""
        ? parseNumber(
            form.compraBManual
          )
        : compraB_calc
      : compraB_calc;

  const ventaA_final =
    form.modoVenta === "MONTO"
      ? form.ventaAManual !== ""
        ? parseNumber(
            form.ventaAManual
          )
        : ventaA_calc
      : ventaA_calc;

  const ventaB_final =
    form.modoVenta === "MONTO"
      ? form.ventaBManual !== ""
        ? parseNumber(
            form.ventaBManual
          )
        : ventaB_calc
      : ventaB_calc;

  /* =========================================================
     PORCENTAJES FINALES
     EN MODO MONTO SALEN DE A + B
  ========================================================= */

  const porcentajeCompraFinal = (() => {
    if (
      form.modoCompra !==
      "MONTO"
    ) {
      return clamp(
        parseNumber(
          form.porcentajeCompra
        ),
        0,
        100
      );
    }

    const baseA =
      compraA_final /
      FACTOR_IVA;

    const baseTotal =
      baseA +
      compraB_final;

    if (
      baseTotal <= 0
    ) {
      return 0;
    }

    return clamp(
      (
        compraB_final /
        baseTotal
      ) * 100,
      0,
      100
    );
  })();

  const porcentajeVentaFinal = (() => {
    if (
      form.modoVenta !==
      "MONTO"
    ) {
      return clamp(
        parseNumber(
          form.porcentajeVenta
        ),
        0,
        100
      );
    }

    const baseA =
      ventaA_final /
      FACTOR_IVA;

    const baseTotal =
      baseA +
      ventaB_final;

    if (
      baseTotal <= 0
    ) {
      return 0;
    }

    return clamp(
      (
        ventaB_final /
        baseTotal
      ) * 100,
      0,
      100
    );
  })();

  /* =========================================================
     CAMBIO DE MODO
  ========================================================= */

  const cambiarModoCompra = (modo) => {
    if (
      modo ===
      form.modoCompra
    ) {
      return;
    }

    if (
      modo === "MONTO"
    ) {
      setForm((prev) => ({
        ...prev,

        modoCompra:
          "MONTO",

        compraAManual:
          to2(
            compraA_calc
          ),

        compraBManual:
          to2(
            compraB_calc
          ),

        autoCompraA: true,
        autoCompraB: true,
      }));

      return;
    }

    setForm((prev) => ({
      ...prev,

      modoCompra:
        "PORCENTAJE",

      porcentajeCompra:
        Number(
          porcentajeCompraFinal.toFixed(
            2
          )
        ),

      compraAManual: "",
      compraBManual: "",

      autoCompraA: false,
      autoCompraB: false,
    }));
  };

  const cambiarModoVenta = (modo) => {
    if (
      modo ===
      form.modoVenta
    ) {
      return;
    }

    if (
      modo === "MONTO"
    ) {
      setForm((prev) => ({
        ...prev,

        modoVenta:
          "MONTO",

        ventaAManual:
          to2(
            ventaA_calc
          ),

        ventaBManual:
          to2(
            ventaB_calc
          ),

        autoVentaA: true,
        autoVentaB: true,
      }));

      return;
    }

    setForm((prev) => ({
      ...prev,

      modoVenta:
        "PORCENTAJE",

      porcentajeVenta:
        Number(
          porcentajeVentaFinal.toFixed(
            2
          )
        ),

      ventaAManual: "",
      ventaBManual: "",

      autoVentaA: false,
      autoVentaB: false,
    }));
  };

  /* =========================================================
     ESTILOS
  ========================================================= */

  const tituloCompra = {
    color: "#0d6efd",
    fontWeight: 700,
  };

  const tituloVenta = {
    color: "#dc3545",
    fontWeight: 700,
  };

  const campoAutomatico = {
    backgroundColor:
      "#f1f3f5",
  };

  const campoMonto = {
    backgroundColor:
      "#ffffff",
  };

  /* =========================================================
     RESET
  ========================================================= */

  useEffect(() => {
    if (show) {
      setForm(
        makeInitialForm()
      );
    }
  }, [show]);

  /* =========================================================
     GUARDAR
  ========================================================= */

  const submit = async (e) => {
    e.preventDefault();

    try {
      /*
        Si hay kg/precio y se carga por monto,
        verificamos que A+B correspondan al total.
      */

      if (
        form.modoCompra ===
          "MONTO" &&
        baseCompra > 0
      ) {
        const baseIngresada =
          compraB_final +
          compraA_final /
            FACTOR_IVA;

        if (
          Math.abs(
            baseIngresada -
              baseCompra
          ) > 0.10
        ) {
          alert(
            "Los importes de Compra A y Compra B no coinciden con el total de compra."
          );

          return;
        }
      }

      if (
        form.modoVenta ===
          "MONTO" &&
        baseVenta > 0
      ) {
        const baseIngresada =
          ventaB_final +
          ventaA_final /
            FACTOR_IVA;

        if (
          Math.abs(
            baseIngresada -
              baseVenta
          ) > 0.10
        ) {
          alert(
            "Los importes de Venta A y Venta B no coinciden con el total de venta."
          );

          return;
        }
      }

      const payload = {
        fecha:
          form.fecha,

        kgCampo:
          parseNumber(
            form.kgCampo
          ),

        kgRecibido:
          parseNumber(
            form.kgRecibido
          ),

        precioCompra:
          parseNumber(
            form.precioCompra
          ),

        precioVenta:
          parseNumber(
            form.precioVenta
          ),

        proveedor:
          form.proveedor ||
          null,

        cliente:
          form.cliente ||
          null,

        transporte:
          form.transporte ||
          null,

        chofer:
          form.chofer ||
          null,

        lote:
          form.lote ||
          null,

        variedad:
          (
            form.variedad ||
            ""
          ).trim(),

        nroDTVCompra:
          (
            form.nroDTVCompra ||
            ""
          ).trim(),

        nroDTVVenta:
          (
            form.nroDTVVenta ||
            ""
          ).trim(),

        facturacompra:
          (
            form.facturacompra ||
            ""
          ).trim(),

        facturaventa:
          (
            form.facturaventa ||
            ""
          ).trim(),

        nota:
          form.nota || "",

        factura:
          !!form.factura,
        
        modoCompra:
          form.modoCompra,

        modoVenta:
          form.modoVenta,

        porcentajeCompra:
          Number(
            porcentajeCompraFinal.toFixed(
              2
            )
          ),

        porcentajeVenta:
          Number(
            porcentajeVentaFinal.toFixed(
              2
            )
          ),

        flete:
          parseNumber(
            form.flete
          ),

        compraA:
          Number(
            compraA_final.toFixed(
              2
            )
          ),

        compraB:
          Number(
            compraB_final.toFixed(
              2
            )
          ),

        ventaA:
          Number(
            ventaA_final.toFixed(
              2
            )
          ),

        ventaB:
          Number(
            ventaB_final.toFixed(
              2
            )
          ),
      };

      console.log(
        "Remito payload:",
        payload
      );

      const resp =
        await postRemito(
          payload
        );

      if (
        resp?.ok === false
      ) {
        throw {
          response: {
            data: resp,
          },
        };
      }

      setForm(
        makeInitialForm()
      );

      setOkModal(true);

      setTimeout(
        () =>
          setOkModal(false),
        800
      );
    } catch (error) {
      const d =
        error?.response?.data;

      const msg =
        d?.err?.message ||
        d?.error?.message ||
        d?.message ||
        "No se pudo crear el remito.";

      alert(msg);
    }
  };

  if (!show) {
    return null;
  }

  /* =========================================================
     RENDER
  ========================================================= */

  return (
    <>
      <Modal
        show={show}
        onHide={onClose}
        size="lg"
      >
        <form
          onSubmit={submit}
        >
          <Modal.Header
            closeButton
          >
            <Modal.Title>
              Nuevo Remito
            </Modal.Title>
          </Modal.Header>

          <Modal.Body>
            <div className="row">

              {/* ================= BASE ================= */}

              <div className="col-md-3 mb-3">
                <label>Fecha</label>

                <input
                  type="date"
                  name="fecha"
                  className="form-control"
                  value={form.fecha}
                  onChange={handle}
                  required
                />
              </div>

              <div className="col-md-3 mb-3">
                <label>
                  KG Campo
                </label>

                <FormattedNumberInput
                  name="kgCampo"
                  value={form.kgCampo}
                  onChange={handle}
                  decimals={0}
                  allowDecimals={false}
                  required
                />
              </div>

              <div className="col-md-3 mb-3">
                <label>
                  KG Recibidos
                </label>

                <FormattedNumberInput
                  name="kgRecibido"
                  value={
                    form.kgRecibido
                  }
                  onChange={handle}
                  decimals={0}
                  allowDecimals={false}
                  required
                />
              </div>

              <div className="col-md-3 mb-3">
                <label>
                  Diferencia
                </label>

                <FormattedNumberInput
                  value={
                    diferencia
                  }
                  decimals={0}
                  allowDecimals={false}
                  allowNegative
                  readOnly
                />
              </div>

              <div className="col-md-3 mb-3">
                <label>
                  Precio Compra ($/kg)
                </label>

                <FormattedNumberInput
                  name="precioCompra"
                  value={
                    form.precioCompra
                  }
                  onChange={handle}
                  decimals={2}
                  currency
                  required
                />
              </div>

              <div className="col-md-3 mb-3">
                <label>
                  Precio Venta ($/kg)
                </label>

                <FormattedNumberInput
                  name="precioVenta"
                  value={
                    form.precioVenta
                  }
                  onChange={handle}
                  decimals={2}
                  currency
                  required
                />
              </div>

              <div className="col-md-2 mb-3">
                <label>
                  Total Compra
                </label>

                <FormattedNumberInput
                  value={
                    totalCompra
                  }
                  decimals={2}
                  currency
                  readOnly
                />
              </div>

              <div className="col-md-2 mb-3">
                <label>
                  Total Venta
                </label>

                <FormattedNumberInput
                  value={
                    totalVenta
                  }
                  decimals={2}
                  currency
                  readOnly
                />
              </div>

              <div className="col-md-2 mb-3">
                <label>
                  Comisión
                </label>

                <FormattedNumberInput
                  value={
                    comision
                  }
                  decimals={2}
                  currency
                  allowNegative
                  readOnly
                />
              </div>

              {/* ================= ENTIDADES ================= */}

              <div className="col-md-4 mb-3">
                <label>
                  Proveedor
                </label>

                <select
                  className="form-control"
                  name="proveedor"
                  value={
                    form.proveedor
                  }
                  onChange={handle}
                  required
                >
                  <option value="">
                    Elija opción
                  </option>

                  {provOpts.map((p) => (
                    <option
                      key={p._id}
                      value={p._id}
                    >
                      {p.razonsocial}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-md-4 mb-3">
                <label>
                  Cliente
                </label>

                <select
                  className="form-control"
                  name="cliente"
                  value={
                    form.cliente
                  }
                  onChange={handle}
                  required
                >
                  <option value="">
                    Elija opción
                  </option>

                  {cliOpts.map((c) => (
                    <option
                      key={c._id}
                      value={c._id}
                    >
                      {c.razonsocial}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-md-4 mb-3">
                <label>
                  Transporte
                </label>

                <select
                  className="form-control"
                  name="transporte"
                  value={
                    form.transporte
                  }
                  onChange={handle}
                  required
                >
                  <option value="">
                    Elija opción
                  </option>

                  {traOpts.map((t) => (
                    <option
                      key={t._id}
                      value={t._id}
                    >
                      {t.transporte}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-md-4 mb-3">
                <label>
                  Chofer
                </label>

                <select
                  className="form-control"
                  name="chofer"
                  value={
                    form.chofer
                  }
                  onChange={handle}
                >
                  <option value="">
                    Elija opción
                  </option>

                  {chofOpts.map((ch) => (
                    <option
                      key={ch._id}
                      value={ch._id}
                    >
                      {ch.nombre}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-md-4 mb-3">
                <label>
                  Lote
                </label>

                <select
                  className="form-control"
                  name="lote"
                  value={
                    form.lote
                  }
                  onChange={handle}
                >
                  <option value="">
                    Elija opción
                  </option>

                  {lotOpts.map((l) => (
                    <option
                      key={l._id}
                      value={l._id}
                    >
                      {l.lote}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-md-4 mb-3">
                <label>
                  Variedad
                </label>

                <input
                  className="form-control"
                  name="variedad"
                  value={
                    form.variedad
                  }
                  onChange={handle}
                  list="variedades-sugeridas"
                  placeholder="Escriba o elija una variedad"
                />

                <datalist id="variedades-sugeridas">
                  {VARIEDADES_SUGERIDAS.map((v) => (
                    <option
                      key={v}
                      value={v}
                    />
                  ))}
                </datalist>
              </div>

              {/* ================= DISTRIBUCION COMPRA / VENTA ================= */}

              <div className="col-md-6 mb-3">
                <div
                  style={{
                    paddingRight: "12px",
                  }}
                >
                  {/* ================= COMPRA ================= */}

                  <label
                    className="mb-2 d-block"
                    style={tituloCompra}
                  >
                    Distribución Compra
                  </label>

                  <div className="btn-group btn-group-sm w-100 mb-3">
                    <button
                      type="button"
                      className={`btn ${
                        form.modoCompra === "PORCENTAJE"
                          ? "btn-primary"
                          : "btn-outline-primary"
                      }`}
                      onClick={() =>
                        cambiarModoCompra("PORCENTAJE")
                      }
                    >
                      Por porcentaje
                    </button>

                    <button
                      type="button"
                      className={`btn ${
                        form.modoCompra === "MONTO"
                          ? "btn-primary"
                          : "btn-outline-primary"
                      }`}
                      onClick={() =>
                        cambiarModoCompra("MONTO")
                      }
                    >
                      Por monto
                    </button>
                  </div>

                  {/* DOCUMENTOS COMPRA */}

                  <div className="row g-2 mb-3">
                    <div className="col-md-4">
                      <label style={tituloCompra}>
                        Nro DTV Compra
                      </label>

                      <input
                        type="text"
                        className="form-control"
                        name="nroDTVCompra"
                        value={form.nroDTVCompra}
                        onChange={handle}
                      />
                    </div>

                    <div className="col-md-4">
                      <label style={tituloCompra}>
                        % Compra B
                      </label>

                      <input
                        type="number"
                        className="form-control"
                        name="porcentajeCompra"
                        min="0"
                        max="100"
                        step="0.01"
                        value={
                          form.modoCompra === "MONTO"
                            ? to2(porcentajeCompraFinal)
                            : form.porcentajeCompra
                        }
                        onChange={handle}
                        readOnly={
                          form.modoCompra === "MONTO"
                        }
                        style={
                          form.modoCompra === "MONTO"
                            ? campoAutomatico
                            : campoMonto
                        }
                      />
                    </div>

                    <div className="col-md-4">
                      <label style={tituloCompra}>
                        Factura Compra
                      </label>

                      <input
                        type="text"
                        className="form-control"
                        name="facturacompra"
                        value={form.facturacompra}
                        onChange={handle}
                      />
                    </div>
                  </div>

                  {/* IMPORTES COMPRA */}

                  <div className="row g-2">
                    <div className="col-md-6">
                      <label>
                        Compra A (blanco + IVA)
                      </label>

                      <FormattedNumberInput
                        value={
                          form.modoCompra === "MONTO"
                            ? form.compraAManual
                            : compraA_calc
                        }
                        onChange={handleCompraAManual}
                        decimals={2}
                        currency
                        readOnly={
                          form.modoCompra !== "MONTO"
                        }
                        style={
                          form.modoCompra === "MONTO"
                            ? campoMonto
                            : campoAutomatico
                        }
                      />
                    </div>

                    <div className="col-md-6">
                      <label>
                        Compra B (negro)
                      </label>

                      <FormattedNumberInput
                        value={
                          form.modoCompra === "MONTO"
                            ? form.compraBManual
                            : compraB_calc
                        }
                        onChange={handleCompraBManual}
                        decimals={2}
                        currency
                        readOnly={
                          form.modoCompra !== "MONTO"
                        }
                        style={
                          form.modoCompra === "MONTO"
                            ? campoMonto
                            : campoAutomatico
                        }
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-md-6 mb-3">
                <div
                  style={{
                    paddingLeft: "14px",
                    borderLeft: "1px solid #dee2e6",
                  }}
                >
                  {/* ================= VENTA ================= */}

                  <label
                    className="mb-2 d-block"
                    style={tituloVenta}
                  >
                    Distribución Venta
                  </label>

                  <div className="btn-group btn-group-sm w-100 mb-3">
                    <button
                      type="button"
                      className={`btn ${
                        form.modoVenta === "PORCENTAJE"
                          ? "btn-danger"
                          : "btn-outline-danger"
                      }`}
                      onClick={() =>
                        cambiarModoVenta("PORCENTAJE")
                      }
                    >
                      Por porcentaje
                    </button>

                    <button
                      type="button"
                      className={`btn ${
                        form.modoVenta === "MONTO"
                          ? "btn-danger"
                          : "btn-outline-danger"
                      }`}
                      onClick={() =>
                        cambiarModoVenta("MONTO")
                      }
                    >
                      Por monto
                    </button>
                  </div>

                  {/* DOCUMENTOS VENTA */}

                  <div className="row g-2 mb-3">
                    <div className="col-md-4">
                      <label style={tituloVenta}>
                        Nro DTV Venta
                      </label>

                      <input
                        type="text"
                        className="form-control"
                        name="nroDTVVenta"
                        value={form.nroDTVVenta}
                        onChange={handle}
                      />
                    </div>

                    <div className="col-md-4">
                      <label style={tituloVenta}>
                        % Venta B
                      </label>

                      <input
                        type="number"
                        className="form-control"
                        name="porcentajeVenta"
                        min="0"
                        max="100"
                        step="0.01"
                        value={
                          form.modoVenta === "MONTO"
                            ? to2(porcentajeVentaFinal)
                            : form.porcentajeVenta
                        }
                        onChange={handle}
                        readOnly={
                          form.modoVenta === "MONTO"
                        }
                        style={
                          form.modoVenta === "MONTO"
                            ? campoAutomatico
                            : campoMonto
                        }
                      />
                    </div>

                    <div className="col-md-4">
                      <label style={tituloVenta}>
                        Factura Venta
                      </label>

                      <input
                        type="text"
                        className="form-control"
                        name="facturaventa"
                        value={form.facturaventa}
                        onChange={handle}
                      />
                    </div>
                  </div>

                  {/* IMPORTES VENTA */}

                  <div className="row g-2 mb-3">
                    <div className="col-md-6">
                      <label>
                        Venta A (blanco + IVA)
                      </label>

                      <FormattedNumberInput
                        value={
                          form.modoVenta === "MONTO"
                            ? form.ventaAManual
                            : ventaA_calc
                        }
                        onChange={handleVentaAManual}
                        decimals={2}
                        currency
                        readOnly={
                          form.modoVenta !== "MONTO"
                        }
                        style={
                          form.modoVenta === "MONTO"
                            ? campoMonto
                            : campoAutomatico
                        }
                      />
                    </div>

                    <div className="col-md-6">
                      <label>
                        Venta B (negro)
                      </label>

                      <FormattedNumberInput
                        value={
                          form.modoVenta === "MONTO"
                            ? form.ventaBManual
                            : ventaB_calc
                        }
                        onChange={handleVentaBManual}
                        decimals={2}
                        currency
                        readOnly={
                          form.modoVenta !== "MONTO"
                        }
                        style={
                          form.modoVenta === "MONTO"
                            ? campoMonto
                            : campoAutomatico
                        }
                      />
                    </div>
                  </div>

                  {/* FLETE */}

                  <div className="row g-2 align-items-end">
                    <div className="col-md-5">
                      <label>
                        Flete ($/kg)
                      </label>

                      <FormattedNumberInput
                        name="flete"
                        value={form.flete}
                        onChange={handle}
                        decimals={2}
                        currency
                      />
                    </div>

                    <div className="col-md-7">
                      <div
                        className="form-check"
                        style={{
                          paddingBottom: "5px",
                        }}
                      >
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="factura"
                          name="factura"
                          checked={!!form.factura}
                          onChange={handle}
                        />

                        <label
                          className="form-check-label"
                          htmlFor="factura"
                          style={{
                            fontSize: "16px",
                          }}
                        >
                          Cobrar
                        </label>

                        <small
                          className="text-muted d-block"
                          style={{
                            fontSize: 12,
                          }}
                        >
                          Incluye flete en la cuenta del cliente
                        </small>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
    

              {/* ================= NOTAS ================= */}

              <div className="col-md-12 mb-3">
                <label>
                  Notas
                </label>

                <textarea
                  className="form-control"
                  rows={2}
                  name="nota"
                  value={
                    form.nota
                  }
                  onChange={handle}
                />
              </div>
            </div>
          </Modal.Body>

          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={
                onClose
              }
            >
              Cancelar
            </Button>

            <Button
              variant="dark"
              type="submit"
            >
              Guardar
            </Button>
          </Modal.Footer>
        </form>
      </Modal>

      <Modal
        show={okModal}
        centered
        backdrop="static"
        keyboard={false}
      >
        <Modal.Body className="text-center">
          <div
            style={{
              fontSize: "3rem",
            }}
          >
            ✅
          </div>

          <h5 className="mt-2">
            ¡Remito grabado!
          </h5>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default AddRemitoForm;